import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatNaira } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Chip, stageChip, priorityChip, statusChip } from "@/components/Chip";
import {
  Search, Users, Target, Ticket, FileText, UserSquare2, Loader2
} from "lucide-react";

/**
 * Global search dropdown that queries /api/search and shows categorized results.
 * Debounced to 200ms.
 */
export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(data);
        setOpen(true);
      } catch (error) {
        console.error("Global search failed:", error);
        setResults(null);
      }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const total = results
    ? results.contacts.length + results.leads.length + results.tickets.length + results.invoices.length + (results.users?.length || 0)
    : 0;

  const go = (path) => {
    setOpen(false);
    setQ("");
    navigate(path);
  };

  return (
    <div ref={containerRef} className="relative w-96 max-w-full">
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      {loading && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />}
      <Input
        data-testid="global-search"
        value={q}
        onChange={e => setQ(e.target.value)}
        onFocus={() => q.length >= 2 && setOpen(true)}
        placeholder="Search contacts, leads, tickets, invoices..."
        className="pl-9 bg-slate-50 border-slate-200 focus-visible:ring-brand-aqua"
      />

      {open && results && (
        <div className="absolute z-30 top-full mt-2 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden animate-fade-in max-h-[70vh] overflow-y-auto" data-testid="search-results">
          {total === 0 ? (
            <div className="text-sm text-slate-500 text-center py-8">No results for "{q}"</div>
          ) : (
            <>
              <Section icon={Users} title="Contacts" items={results.contacts}
                       renderItem={c => (
                         <button key={c.id} onClick={() => go("/contacts")} className="w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-slate-50" data-testid={`search-contact-${c.id}`}>
                           <div className="w-7 h-7 rounded-md bg-brand-aqua-light text-brand-aqua-hover flex items-center justify-center text-xs font-semibold">{c.name?.[0]}</div>
                           <div className="flex-1 min-w-0">
                             <div className="text-sm text-slate-800 truncate">{c.name}</div>
                             <div className="text-[11px] text-slate-500 truncate">{c.company} · {c.email}</div>
                           </div>
                           <Chip>{c.business_unit}</Chip>
                         </button>
                       )} />
              <Section icon={Target} title="Leads" items={results.leads}
                       renderItem={l => (
                         <button key={l.id} onClick={() => go("/leads")} className="w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-slate-50" data-testid={`search-lead-${l.id}`}>
                           <Target className="w-4 h-4 text-slate-400 shrink-0" />
                           <div className="flex-1 min-w-0">
                             <div className="text-sm text-slate-800 truncate">{l.title}</div>
                             <div className="text-[11px] text-slate-500 truncate">{l.contact_name} · {l.company}</div>
                           </div>
                           <div className="text-xs num font-medium">{formatNaira(l.value)}</div>
                           <Chip variant={stageChip(l.stage)}>{l.stage}</Chip>
                         </button>
                       )} />
              <Section icon={Ticket} title="Tickets" items={results.tickets}
                       renderItem={t => (
                         <button key={t.id} onClick={() => go("/tickets")} className="w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-slate-50" data-testid={`search-ticket-${t.id}`}>
                           <Ticket className="w-4 h-4 text-slate-400 shrink-0" />
                           <div className="flex-1 min-w-0">
                             <div className="text-sm text-slate-800 truncate">{t.subject}</div>
                             <div className="text-[11px] text-slate-500 truncate">{t.contact_name}</div>
                           </div>
                           <Chip variant={priorityChip(t.priority)}>{t.priority}</Chip>
                           <Chip variant={statusChip(t.status)}>{t.status}</Chip>
                         </button>
                       )} />
              <Section icon={FileText} title="Invoices" items={results.invoices}
                       renderItem={i => (
                         <button key={i.id} onClick={() => go("/invoices")} className="w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-slate-50" data-testid={`search-invoice-${i.id}`}>
                           <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                           <div className="flex-1 min-w-0">
                             <div className="text-sm text-slate-800 truncate num">{i.invoice_number}</div>
                             <div className="text-[11px] text-slate-500 truncate">{i.contact_name}</div>
                           </div>
                           <div className="text-xs num font-medium">{formatNaira(i.total)}</div>
                           <Chip variant={statusChip(i.status)}>{i.status}</Chip>
                         </button>
                       )} />
              {results.users && results.users.length > 0 && (
                <Section icon={UserSquare2} title="Staff" items={results.users}
                         renderItem={u => (
                           <button key={u.id} onClick={() => go(`/employee/${u.id}`)} className="w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-slate-50" data-testid={`search-user-${u.id}`}>
                             <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-xs font-semibold">{u.name?.[0]}</div>
                             <div className="flex-1 min-w-0">
                               <div className="text-sm text-slate-800 truncate">{u.name}</div>
                               <div className="text-[11px] text-slate-500 truncate">{u.email}</div>
                             </div>
                             <Chip variant="aqua">{u.role?.replace("_", " ")}</Chip>
                           </button>
                         )} />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ icon: Icon, title, items, renderItem }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        <Icon className="w-3 h-3" /> {title} ({items.length})
      </div>
      <div>{items.map(renderItem)}</div>
    </div>
  );
}
